import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastIcon = ({ type }) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    case 'error':
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    case 'info':
    default:
      return <Info className="w-5 h-5 text-blue-500" />;
  }
};

const getToastStyles = (type) => {
  switch (type) {
    case 'success':
      return {
        container: 'border-l-4 border-emerald-500',
        icon: 'bg-emerald-100 dark:bg-emerald-900/40',
        progress: 'bg-emerald-500'
      };
    case 'error':
      return {
        container: 'border-l-4 border-red-500',
        icon: 'bg-red-100 dark:bg-red-900/40',
        progress: 'bg-red-500'
      };
    case 'warning':
      return {
        container: 'border-l-4 border-amber-500',
        icon: 'bg-amber-100 dark:bg-amber-900/40',
        progress: 'bg-amber-500'
      };
    case 'info':
    default:
      return {
        container: 'border-l-4 border-blue-500',
        icon: 'bg-blue-100 dark:bg-blue-900/40',
        progress: 'bg-blue-500'
      };
  }
};

const getToastTitle = (type) => {
  switch (type) {
    case 'success':
      return 'Success';
    case 'error':
      return 'Error';
    case 'warning':
      return 'Warning';
    case 'info':
    default:
      return 'Info';
  }
};

// Individual Toast Component with its own animation state
const Toast = ({ toast, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  const styles = getToastStyles(toast.type);
  const title = toast.title || getToastTitle(toast.type);

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setIsEntering(false), 50);
    return () => clearTimeout(enterTimer);
  }, []);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  // Auto-remove with exit animation
  useEffect(() => {
    if (toast.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration - 300); // Start exit animation 300ms before removal
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  return (
    <div
      className={`
        flex flex-col overflow-hidden
        rounded-xl shadow-2xl
        min-w-[360px] max-w-[420px]
        bg-white dark:bg-gray-800
        transition-all duration-300 ease-out
        ${styles.container}
        ${isEntering ? 'translate-x-full opacity-0' : ''}
        ${isExiting ? 'translate-x-full opacity-0 scale-95' : 'translate-x-0 opacity-100 scale-100'}
      `}
      style={{
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1)'
      }}
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`flex-shrink-0 p-2 rounded-full ${styles.icon}`}>
          <ToastIcon type={toast.type} />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            {title}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 leading-relaxed">
            {toast.message}
          </p>
        </div>
        <button
          onClick={handleRemove}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-all duration-200 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 hover:rotate-90"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Animated Progress bar */}
      <div className="h-1 w-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full ${styles.progress} transition-all ease-linear`}
          style={{
            width: '100%',
            animation: `toast-progress ${toast.duration}ms linear forwards`
          }}
        />
      </div>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000, title = null) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration, title, createdAt: Date.now() };

    setToasts(prev => [...prev, toast]);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const handleToastCall = useCallback((type, defaultDuration) => (message, titleOrDuration, duration) => {
    let title = null;
    let finalDuration = duration ?? defaultDuration;

    if (typeof titleOrDuration === 'string') {
      title = titleOrDuration;
    } else if (typeof titleOrDuration === 'number') {
      finalDuration = titleOrDuration;
    }

    addToast(message, type, finalDuration, title);
  }, [addToast]);

  const success = useCallback(handleToastCall('success', 4000), [handleToastCall]);
  const error = useCallback(handleToastCall('error', 6000), [handleToastCall]);
  const warning = useCallback(handleToastCall('warning', 4000), [handleToastCall]);
  const info = useCallback(handleToastCall('info', 4000), [handleToastCall]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-20 right-4 z-[100] flex flex-col gap-3">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes toast-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
